import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormProvider } from '@/components/react-hook-form';
import { getIntegrationProviderDisplayName } from '@/modules/assetProviderIntegrations/shared/constants';
import { useAssetProviderDataLinkingState } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { IntegrationClientsTypeahead } from '@/modules/assetProviderIntegrations/shared/IntegrationClientTypeahead/IntegrationClientsTypeahead';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import { useSelectClientsStepDataQuery } from '@/modules/household/LinkHouseholdDataTrowser/SelectClientsStep/graphql/SelectClientsStep.generated';
import { COLORS } from '@/styles/tokens/colors';
import { IntegrationClientKind } from '@/types/schema';
import { englishJoinStringList } from '@/utils/formatting/strings';

import { useManageIntegrationDataContext } from '../../context/manageIntegrationData.context';
import { SelectClientsForm } from './useSelectClientsForm';

export interface SelectClientsPaneProps {
  form: SelectClientsForm;
}

/**
 * Step 1: pick which integration clients this household is linked to.
 *
 * The form belongs to the modal; this renders it and keeps it fed. The query
 * lives here rather than alongside the form so it only runs once the pane is
 * opened, and runs again whenever the user comes back to it.
 */
export function SelectClientsPane({ form }: SelectClientsPaneProps) {
  const { householdId, isSaving } = useManageIntegrationDataContext();
  const canManageIntegrationData = useCanManageIntegrationData();
  const { integrationsAvailableForDataLinking } =
    useAssetProviderDataLinkingState();
  const { formMethods, seed } = form;

  const { data, loading } = useSelectClientsStepDataQuery({
    variables: { householdId },
    skip: !householdId,
    // Show what we have immediately, then reconcile with the server — the
    // household's clients can change from elsewhere while the modal is open.
    fetchPolicy: 'cache-and-network',
  });

  const household = data?.households?.edges?.[0]?.node;
  useEffect(() => {
    if (!household) return;
    seed(
      household.integrationClients
        ?.filter((client) => client.kind !== IntegrationClientKind.Salesforce)
        .map((client) => client.id) ?? []
    );
  }, [household, seed]);

  const integrationDisplayNames = englishJoinStringList(
    integrationsAvailableForDataLinking.map((provider) =>
      getIntegrationProviderDisplayName(provider)
    ),
    'or'
  );

  return (
    <FormProvider {...formMethods}>
      {/* Locked while committing, so values can't change under the mutation. */}
      <FormFieldsDisabledProvider isDisabled={isSaving}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
          // Fills the step body so the copy and the input sit centered in it.
          minHeight="100%"
        >
          {/* No width of its own: the copy is one line per row in the design,
              so the block hugs it and stays centered. */}
          <Box textAlign="center">
            <Typography variant="title-s" component="h2">
              {`Select ${integrationDisplayNames} clients`}
            </Typography>
            <Typography
              display="block"
              variant="body-s"
              color={COLORS.TEXT.SECONDARY}
              mt={0.5}
            >
              {`Select the ${integrationDisplayNames} clients you would like to connect to this household`}
            </Typography>
          </Box>

          <Box width={320}>
            <IntegrationClientsTypeahead
              fieldName="integrationClientIds"
              label="Integration clients"
              providers={integrationsAvailableForDataLinking}
              loading={loading}
              hideLabel
              control={formMethods.control}
              disabled={!canManageIntegrationData}
              placeholder="Select clients"
            />
          </Box>
        </Box>
      </FormFieldsDisabledProvider>
    </FormProvider>
  );
}
